import { FollowType, notEmpty } from "@product/scmp-sdk";
import first from "lodash/first";
import { forwardRef } from "react";
import { graphql, useFragment } from "react-relay";

import type { DefaultComponentsProvider } from "~/components/content/content-item-render";
import type { ContentItemRenderVariantProps } from "~/components/content/content-item-render/types";
import { sendGATracking } from "~/components/tracking/google-analytics/apis";
import type { FollowButtonSource } from "~/components/tracking/google-analytics/types";
import type { homeSectionLeadSecondaryContentItemContent$key } from "~/queries/__generated__/homeSectionLeadSecondaryContentItemContent.graphql";
import type { CustomQueryParameters } from "~/types/gtm";

import {
  ActionBar,
  Container,
  Headline,
  LeadingImage,
  LinkHighlightArea,
  Primary,
  StyledContentCoverImage,
  StyledEntityLink,
  StyledHomeFollowButton,
  Summary,
  Topic,
  TopicContainer,
} from "./styles";

export type Props = {
  className?: string;
  customQueryParameters?: CustomQueryParameters;
  provider: DefaultComponentsProvider;
  reference: homeSectionLeadSecondaryContentItemContent$key;
  variantProps?: ContentItemHomeSectionLeadSecondaryVariantProps;
} & ContentItemRenderVariantProps;

export type ContentItemHomeSectionLeadSecondaryVariantProps = {
  followButtonSource?: FollowButtonSource;
  withBookmark?: boolean;
  withComment?: boolean;
};

export const ContentItemHomeSectionLeadSecondary = forwardRef<HTMLDivElement, Props>(
  (
    {
      className,
      customQueryParameters,
      onClick,
      provider,
      reference: reference_,
      variantProps = {
        withComment: false,
      },
    },
    reference,
  ) => {
    const content = useFragment(
      graphql`
        fragment homeSectionLeadSecondaryContentItemContent on Content {
          entityId
          topics {
            ...followButtonBase
          }
          ...entityLink
          ...contentCoverImageContent
        }
      `,
      reference_,
    );

    const isSponsorArticle = notEmpty(provider.sponsorTag?.());
    const topic = first(content?.topics ?? []);

    return (
      <Container className={className} ref={reference}>
        <LeadingImage>
          <StyledEntityLink reference={content} onClick={() => onClick?.(content.entityId)}>
            <StyledContentCoverImage
              reference={content}
              responsiveVariants={{
                desktopUp: "size1200x800",
                mobileUp: "size1200x800",
                tabletUp: "size1200x800",
              }}
            />
          </StyledEntityLink>
        </LeadingImage>
        <Primary>
          <TopicContainer>
            <Topic>{provider.topicLink?.()}</Topic>
            {topic && (
              <StyledHomeFollowButton
                buttonVariant="square"
                reference={topic}
                type={FollowType.Topic}
                onClick={isActionFollow => {
                  if (!variantProps.followButtonSource || !isActionFollow) return;
                  sendGATracking({
                    action:
                      `Notification Center/Follow/${variantProps.followButtonSource}/Click` as const,
                    category: "Notification Center",
                  });
                }}
              />
            )}
          </TopicContainer>

          <StyledEntityLink
            query={customQueryParameters}
            reference={content}
            onClick={() => onClick?.(content.entityId)}
          >
            <LinkHighlightArea>
              <Headline>
                {provider.headline({
                  hideTooltip: true,
                  preferSocialHeadline: true,
                })}
              </Headline>
              <Summary>
                {provider.summary({
                  isTextOverflowEllipsis: true,
                })}
              </Summary>
            </LinkHighlightArea>
          </StyledEntityLink>

          {isSponsorArticle ? (
            provider.sponsorTag?.({ variant: "home" })
          ) : (
            <ActionBar>
              {provider.actionBar({
                commentVariant: "reverse",
                isBookmarkVisible: variantProps?.withBookmark ?? false,
                isCommentsVisible: variantProps?.withComment ?? false,
              })}
            </ActionBar>
          )}
        </Primary>
      </Container>
    );
  },
);

ContentItemHomeSectionLeadSecondary.displayName = "ContentItemHomeSectionLeadSecondary";
